<template>
    <div>
        <v-card>
            <v-form
                ref="form"
                @submit.prevent="submitForm"
            >
                <v-card-subtitle>Enter the details below to verify ACU approval.</v-card-subtitle>
                <v-card-text>
                    <v-text-field
                        v-model="data.opan"
                        v-mask="'X-########'"
                        color="teal"
                        label="Approval Number"
                        :rules="[v=>!!v||'This field is required']"
                        clearable
                        placeholder="C-00000000"
                        autocomplete="nope"
                        :random-field-name="`approval_${randomFieldName}`"
                        @input="formatOpan"
                    />
                    <v-text-field
                        v-model="data.membN"
                        v-mask="'######'"
                        color="teal"
                        label="Member Number"
                        placeholder="000000"
                        :rules="[v=>!!v||'This field is required']"
                        autocomplete="nope"
                        :random-field-name="`member_${randomFieldName}`"
                        clearable
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        type="submit"
                        color="teal"
                        class="white--text"
                    >
                        <v-icon> {{ mdiMagnifyScan }}</v-icon>&nbsp;VERIFY AND CONFIRM ACU APPROVAL
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
        <v-dialog
            v-model="showDialog"
            width="500"
            persistent
            class="blackFont"
            style="z-index: 100px;"
        >
            <v-card>
                <v-card-title class="text-h5">
                    <span v-if="isSessionExpired">Session Ended</span>
                    <span v-else>Verification Status</span>
                </v-card-title>
                <v-card-text v-if="isSessionExpired" style="height: auto; min-height: 95px;">
                    <v-fade-transition hide-on-leave>
                        <p style="padding-top: 25px;" class="blackFont">
                            Your session has expired for security reasons. Please <span class="errHighlight">log in again</span> to continue.
                        </p>
                    </v-fade-transition>
                </v-card-text>
                <v-card-text v-else>
                    <v-fade-transition hide-on-leave>
                        <p v-if="patientNoAccess" class="notEligible">
                            <span class="errHighlight">ACCESS DENIED.</span> You have no access to view or interact with this page or resource.
                        </p>
                        <p v-else-if="invalidOpan&&errMessage&&!loading" class="notFound blackFont">
                            This approval number is <span class="errHighlight">NOT VALID</span>; either it has expired, it is scheduled on a future date, or it is non-existent. Please check your input or contact HMI.
                        </p>
                    </v-fade-transition>
                    <v-fade-transition hide-on-leave>
                        <p v-show="loading" class="loadingNotEligible">
                            <v-progress-linear
                                indeterminate
                                color="#009688"
                            />
                        </p>
                    </v-fade-transition>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        v-if="!patientNoAccess&&!isSessionExpired&&!loading&&!invalidOpan"
                        class="txtHighlight"
                        color="error darken-4"
                        text
                        @click="closeModal"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        v-else-if="!patientNoAccess&&!isSessionExpired"
                        class="txtHighlight"
                        color="error darken-4"
                        text
                        @click="closeModal"
                    >
                        Close
                    </v-btn>
                    <v-btn
                        v-else
                        class="txtHighlight"
                        color="#009688"
                        text
                        @click="closeModal"
                    >
                        Okay
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { mask } from 'vue-the-mask'
import { mdiMagnifyScan } from '@mdi/js'
export default {
    directives: { mask },
    data: ()=>({
        mdiMagnifyScan,
        randomFieldName: Math.random().toString(36).substr(2, 9),
        patientNoAccess: false,
        isVerificationHasResult: false,
        errMessage: null,
        invalidOpan: false,
        loading: false,
        valid: false,
        data: {
            opan: null,
            membN: null
        }
    }),
    computed: {
        ...mapGetters(['verifyAcuApprovalData', 'isSessionExpired']),
        showDialog: {
            get() {
                return this.isVerificationHasResult
            },
            set() {
                this.isVerificationHasResult = false
            }
        }
    },
    watch: {
        verifyAcuApprovalData(newVal) {
            this.loading = false
            this.patientNoAccess = false
            if (newVal && newVal.status === 200) {
                this.invalidOpan = false
                this.$emit('acuApprovalValid')
            } else if (newVal && newVal.status === 406) {
                this.invalidOpan = true
                this.errMessage = newVal.state
            } else if (newVal && newVal.status === 403) {
                this.patientNoAccess = true
            }
        }
    },
    methods: {
        ...mapActions(['getCsrfToken', 'verifyAcuApproval']),
        closeModal() {
            this.showDialog = false
        },
        formatOpan() {
            if (this.data.opan) {
                let value = this.data.opan.replace(/[^A-Z0-9]/gi, '') // Remove non-alphanumeric characters
                if (value.length > 1) {
                    value = value[0] + '-' + value.slice(1, 9) // Insert '-' after the first character
                }
                this.data.opan = value.toUpperCase() // Ensure uppercase format
            }
        },
        async submitForm() {
            if (this.$refs && this.$refs.form.validate()) {
                this.isVerificationHasResult = true
                this.loading = true
                const params = {
                    MSUU_C: this.$cookies.get('provider_id'),
                    OPAN_N: this.data.opan,
                    MEMB_N: this.data.membN
                }
                this.verifyAcuApproval(params)
            }
        }
    }
}
</script>

<style scoped>
.docInfoBodyNote {
    font-weight: 700;
}
.docInfoBodySubmit {
    padding: 2% 0;
}
.docInfoBodyBtn {
    margin-bottom: 7%;
}
    .loadingNotEligible {
        padding-top: 40px;
    }
    .notFound {
        margin: 0;
        word-spacing: .9px;
        letter-spacing: .5px;
        padding-top: 17px;
    }
    .blackFont {
        color: black;
    }
    .errSearchDoctor {
        padding: 5px 5px 0 5px;
        margin: 0;
    }
    .errHighlight {
        font-weight: 800;
        color: darkred;
    }
    .txtHighlight {
        font-weight: 600;
    }
    .notEligible {
        color: black;
        padding-top: 20px;
    }
</style>

